
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Skeleton } from "@/components/ui/skeleton";

import DashboardAnalytics from "./DashboardAnalytics";
import { RootState } from "@/store/store";
import { stepsData as StepsDataType } from "@/types/globalTypes";
import DashboardSteps from "./DashboardSteps";

function DashBoard() {
  const [stepsData, setStepsData] = useState<StepsDataType>();
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);
  const [showAllStatus, setShowAllStatus] = useState(false);

  const userData = useSelector((state: RootState) => state.user.userData);
  const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
  const baseUrl = import.meta.env.VITE_BASE_URL;

  // This function only runs if the user is owner/superadmin
  const getStepsStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/dashboard/status`, {
        headers: { Authorization: token },
      });

      if (response.data.success) {
        setStepsData(response.data);
        setActive(response.data.published === true);
      }
    } catch (error) {
      console.error("Failed to fetch steps status:", error);
    }
  }, [baseUrl, token]);

  // Check if all required setup steps are complete
  const checkStatus = useCallback((data?: StepsDataType) => {
    const d = data || stepsData;
    const allComplete =
      d?.hasBranch &&
      d?.hasLogo &&
      d?.hasName &&
      d?.hasPhone &&
      d?.hasProduct &&
      d?.hasDomain;

    setShowAllStatus(!allComplete);
  }, [stepsData]);

  // Main loader effect
  useEffect(() => {
    const loadInitialData = async () => {
      if (!token || !userData) return;

      setLoading(true);

      if (userData.role === "owner" || userData.role === "superadmin") {
        await getStepsStatus();
      } else {
        setActive(true);
      }

      setLoading(false);
    };

    loadInitialData();
  }, [token, userData, getStepsStatus]);

  // Check steps data status when it's loaded
  useEffect(() => {
    if (stepsData) {
      checkStatus(stepsData);
    }
  }, [stepsData, checkStatus]);

  return (
    <>
      {!loading ? (
        active ? (
          <DashboardAnalytics />
        ) : (
          <DashboardSteps
            stepsData={stepsData as StepsDataType}
            getStepsStatus={getStepsStatus}
            showAllStatus={showAllStatus}
          />
        )
      ) : (
        <>
          <div>
            <Skeleton className="w-[500px] h-[50px] rounded-md" />
          </div>
          <div className="flex gap-5 flex-wrap mt-10">
            <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
            <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
            <Skeleton className="w-[200px] h-[100px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
          </div>
          <div className="mt-10">
            <Skeleton className="w-full h-[500px] dark:bg-slate-600 dark:bg-opacity-35 rounded-md" />
          </div>
        </>
      )}
    </>
  );
}

export default DashBoard;
